import React, { useState } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Send, Coins, Volume2, VolumeX } from "lucide-react";
import { VoiceRecorder } from "../../VoiceRecorder";
import { toast } from "sonner";

interface ChatInputProps {
  totalCredits: number;
  voiceMode: boolean;
  isStreaming?: boolean;
  onSendMessage: (text: string) => void;
  onToggleVoiceMode: () => void;
}

export const ChatInput: React.FC<ChatInputProps> = ({
  totalCredits,
  voiceMode,
  isStreaming = false,
  onSendMessage,
  onToggleVoiceMode
}) => {
  const [inputText, setInputText] = useState('');
  const [isListening, setIsListening] = useState(false);

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSend = () => {
    if (inputText.trim() && !isStreaming) {
      onSendMessage(inputText);
      setInputText('');
    }
  };

  const handleTranscription = (text: string) => {
    onSendMessage(text);
    toast.success('Message sent from voice input');
  };

  return (
    <div className="border-t border-border/50 p-3 sm:p-6 bg-gradient-calm">
      <div className="flex items-center justify-between mb-3 sm:mb-4 pb-2 sm:pb-3 border-b border-border/30">
        <div className="flex items-center gap-1.5 sm:gap-2 text-xs sm:text-sm">
          <Coins className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-primary" />
          <span className="font-medium">Credits:</span>
          <span className={`font-bold ${totalCredits <= 10 ? 'text-destructive' : 'text-primary'}`}>{totalCredits}</span>
          <span className="text-muted-foreground text-[10px] sm:text-xs">(3 per exchange)</span>
        </div>
        {totalCredits <= 10 && (
          <Button variant="outline" size="sm" onClick={() => window.location.href = '/credits'} className="text-[10px] sm:text-xs h-7 px-2">
            Get Credits
          </Button>
        )}
      </div>

      <div className="flex items-center gap-2 sm:gap-4">
        <div className="flex-1 flex items-center gap-1.5 sm:gap-2">
          <Input 
            value={inputText} 
            onChange={(e) => setInputText(e.target.value)} 
            onKeyPress={handleKeyPress} 
            placeholder={totalCredits < 3 ? "Out of credits" : "How are you feeling?"} 
            className="flex-1 bg-background/80 backdrop-blur-sm border-border/50 focus:border-primary transition-gentle text-sm"
            disabled={totalCredits < 3 || isStreaming}
          />
          <Button onClick={handleSend} variant="therapy" size="icon" disabled={!inputText.trim() || totalCredits < 3 || isStreaming} className="h-9 w-9 shrink-0">
            <Send className="h-4 w-4" />
          </Button>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Button
                  variant={voiceMode ? "default" : "outline"}
                  size="icon"
                  onClick={onToggleVoiceMode}
                  className={`h-9 w-9 shrink-0 ${voiceMode ? 'shadow-therapy' : ''}`}
                >
                  {voiceMode ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </Button>
              </TooltipTrigger>
              <TooltipContent side="top" className="max-w-xs">
                <p className="font-semibold mb-1">Voice Mode</p>
                <p className="text-xs">
                  {voiceMode 
                    ? 'Responses spoken first in Amanda\'s voice' 
                    : 'Enable voice responses'}
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
          <VoiceRecorder onTranscription={handleTranscription} isListening={isListening} setIsListening={setIsListening} />
        </div>
      </div>
      
      <div className="text-[10px] sm:text-xs text-muted-foreground mt-2 text-center space-y-1">
        {isListening ? (
          <p className="text-primary font-medium">🎤 Listening... speak freely, then click mic to send</p>
        ) : voiceMode ? (
          <p>
            <span className="text-primary font-medium">🔊 Voice Mode</span> • 
            Amanda will speak responses first
          </p>
        ) : totalCredits >= 3 ? (
          <p>Press microphone to speak or type your message • All conversations saved</p>
        ) : (
          <p>Purchase credits to continue (3 credits per exchange)</p>
        )}
      </div>
    </div>
  );
};
